import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Paper, Typography, Button, Icon } from '@safedns/skydns-ui-react-lib';

import CircularProgress from '../UI/circular-progress';

import {
  BASE_URL,
  useGetUserQuery,
  useGetAuthKeyQuery,
  useRequestAgentQuery,
  useRequestBuildAgentMutation,
} from '../../services/ADapi';

import sprite from '../../icons/sprite.svg';
import PinDialog from './PinDialog';
import RebuildAgentDialog from './RebuildAgentDialog';
import styles from './ConfigurateAd.module.scss';

const Caption = ({ children, ...props }) => (
  <Typography variant="caption" {...props}>
    {children}
  </Typography>
);

const ButtonCaption = ({ agentBuildStatus }) => {
  switch (agentBuildStatus) {
    // null means that a user doesn't have a built agent yet
    case null:
      return <Caption>Сборка агента занимает около минуты</Caption>;
    case 'Send':
      return <Caption>Сборка в процессе. Обычно занимает ~1 мин.</Caption>;
    case 'Success':
      return <Caption>Агент успешно собран</Caption>;
    case 'Fail':
      return (
        <Caption color="error.main">
          Не удалось собрать. Попробуйте еще раз или обратитесь в поддержку
        </Caption>
      );
    default:
      return <Caption color="error.main">Неопознанная ошибка</Caption>;
  }
};

const getIcon = (status) => {
  if (status === 'Send') {
    return <CircularProgress disableShrink color="secondary" size="16px" />;
  }

  return (
    <Icon
      id="download"
      sprite={sprite}
      width={16}
      height={16}
      color="secondary"
    />
  );
};

const ConfigurateAd = () => {
  const {
    data: { user_id },
  } = useGetUserQuery();
  const [isDialogOpen, setDialogState] = useState(false);
  const [isRebuildDialogOpen, setRebuildDialogState] = useState(false);
  const [pin, setPin] = useState('');
  const [agentBuildStatus, setAgentBuildStatus] = useState(null);

  const { error: authKeyError } = useGetAuthKeyQuery(user_id);

  const isAgentReady = agentBuildStatus === 'Success';

  const { data: agent } = useRequestAgentQuery(user_id, {
    skip: !user_id,
    pollingInterval: isAgentReady ? undefined : 15000,
  });

  useEffect(() => {
    const buildStatus = agent?.status ?? null;
    setAgentBuildStatus(buildStatus);
  }, [agent?.status]);

  useEffect(() => {
    if (authKeyError) {
      setAgentBuildStatus(null);
    }
  }, [authKeyError]);

  const [requestBuildAgent] = useRequestBuildAgentMutation();

  const requestBuildAgentHandler = async () => {
    setDialogState(false);

    try {
      const response = await requestBuildAgent({
        user_id,
        pin,
      }).unwrap();

      setAgentBuildStatus(response.status);
      setPin('');
    } catch (error) {
      setDialogState(false);
      setAgentBuildStatus(error?.status);
      setPin('');
      console.error(error);
    }
  };

  const rebuildBuildAgendHandler = () => {
    setDialogState((prevState) => !prevState);
    setRebuildDialogState((prevState) => !prevState);
  };

  const dialogStateHandler = () => {
    setDialogState((prevState) => !prevState);
  };

  const rebuildDialogStateHandler = () => {
    setRebuildDialogState((prevState) => !prevState);
  };

  return (
    <Paper title="Настройка Active Directory">
      <div className={styles['dialog-content']}>
        <Typography
          variant="body2"
          component="p"
          className={styles['description-text']}
          sx={{ paddingBottom: 24 }}
        >
          Следуйте{' '}
          <Link
            to="https://docs.skydns.ru/books/ustanovka-i-nastroika-filtracii/page/nastroika-active-directory"
            target="_blank"
          >
            инструкциям
          </Link>{' '}
          по настройке Active Directory и загрузке агента.
        </Typography>
        <div className={styles['buttons-grid']}>
          <div className={styles['grid-item']}>
            <Button
              href={
                isAgentReady
                  ? `${BASE_URL}/agents/rest_api/v1/users/${user_id}/build/download/`
                  : undefined
              }
              variant="primary"
              iconRight={getIcon(agentBuildStatus)}
              isDisabled={
                agentBuildStatus === 'Send' || authKeyError !== undefined
              }
              onClick={isAgentReady ? undefined : dialogStateHandler}
            >
              {isAgentReady ? 'Загрузить агент' : 'Собрать агент'}
            </Button>
            <ButtonCaption agentBuildStatus={agentBuildStatus} />
          </div>
          {isAgentReady && !authKeyError ? (
            <div className={styles['grid-item']}>
              <Button
                variant="secondary"
                onClick={rebuildDialogStateHandler}
                width="100%"
              >
                Пересобрать агент
              </Button>
              <Caption>Если PIN утерян, потребуется пересобрать агент</Caption>
            </div>
          ) : null}
        </div>
      </div>
      {isDialogOpen ? (
        <PinDialog
          onDialogAction={requestBuildAgentHandler}
          onCancel={dialogStateHandler}
          pin={pin}
          setPin={setPin}
        />
      ) : null}
      {isRebuildDialogOpen ? (
        <RebuildAgentDialog
          onCancel={rebuildDialogStateHandler}
          onDialogAction={rebuildBuildAgendHandler}
        />
      ) : null}
    </Paper>
  );
};

export default ConfigurateAd;
